import type { AppState, CalendarKind, IconId, ISODate, LocalEvent, OccurrenceKey, SourceItem, TagInfo, Timing } from './model.ts';
import { Temporal, addDays, compareDates, daysBetween, daysCovered, startDay, timingInstants } from './time.ts';
import { expandOccurrences } from './recurrence.ts';

export type ItemRef = { kind: 'local'; id: string } | { kind: 'source'; key: string };

export interface DisplayItem {
  /** Unik per förekomst: "<ref>@<occurrenceKey>". */
  uid: string;
  ref: ItemRef;
  occurrenceKey: OccurrenceKey;
  title: string;
  /** Originaltext för importerade poster (taggen bevarad). */
  sourceSummary?: string;
  tag?: TagInfo;
  timing: Timing;
  calendarId: string;
  calendarKind: CalendarKind;
  calendarName: string;
  color: string;
  categoryId: string;
  categoryName: string;
  icon: IconId;
  location?: string;
  notes?: string;
  done: boolean;
  /** Påverkar inte krockvarningar (TRANSP:TRANSPARENT). */
  transparent: boolean;
  /** Källdatum – kan inte dras eller flyttas. */
  locked: boolean;
  recurring: boolean;
  adjusted: boolean;
  review?: string | null;
  missingInLatest?: boolean;
  linkedSourceKey?: string;
  priority?: 'normal' | 'high';
}

export interface ViewFilter {
  /** Kalenderslag som visas. Standard: alla. */
  kinds?: CalendarKind[];
  calendarIds?: string[];
  query?: string;
  includeDone?: boolean;
}

function sourceCalendar(state: AppState, s: SourceItem) {
  const src = state.sources[s.sourceId];
  return (src && state.calendars[src.calendarId]) || Object.values(state.calendars).find((c) => c.sourceId === s.sourceId);
}

export function collectItems(state: AppState, from: ISODate, toExclusive: ISODate, filter: ViewFilter = {}): DisplayItem[] {
  const tz = state.settings.timeZone;
  const out: DisplayItem[] = [];
  const kinds = filter.kinds ? new Set(filter.kinds) : null;
  const q = filter.query?.trim() ? filter.query.trim().toLocaleLowerCase('sv') : null;
  /** Billig textförfiltrering på postnivå innan förekomster expanderas. */
  const textHit = (...fields: Array<string | undefined>) => !q || fields.some((f) => !!f && f.toLocaleLowerCase('sv').includes(q));
  const calIds = filter.calendarIds ? new Set(filter.calendarIds) : null;

  for (const ev of Object.values(state.events)) {
    if (ev.deleted) continue;
    const cal = state.calendars[ev.calendarId];
    if (!cal || !cal.visible) continue;
    if (kinds && !kinds.has(cal.kind)) continue;
    if (calIds && !calIds.has(cal.id)) continue;
    const cat = state.categories[ev.categoryId];
    if (q && !textHit(ev.title, ev.location, ev.notes, cat?.name, ...Object.values(ev.overrides ?? {}).flatMap((o) => [o.title, o.location, o.notes]))) continue;
    for (const occ of expandOccurrences(ev, from, toExclusive, tz)) {
      const done = ev.recurrence ? !!ev.doneOccurrences?.[occ.key] : !!ev.done;
      out.push({
        uid: `${ev.id}@${occ.key}`,
        ref: { kind: 'local', id: ev.id },
        occurrenceKey: occ.key,
        title: occ.override?.title ?? ev.title,
        timing: occ.timing,
        calendarId: cal.id,
        calendarKind: cal.kind,
        calendarName: cal.name,
        color: cal.color,
        categoryId: ev.categoryId,
        categoryName: cat?.name ?? 'Annat',
        icon: ev.icon,
        ...(occ.override?.location ?? ev.location ? { location: occ.override?.location ?? ev.location } : {}),
        ...(occ.override?.notes ?? ev.notes ? { notes: occ.override?.notes ?? ev.notes } : {}),
        done,
        transparent: false,
        locked: false,
        recurring: occ.recurring,
        adjusted: occ.adjusted,
        ...(ev.linkedSourceKey ? { linkedSourceKey: ev.linkedSourceKey } : {}),
        ...(ev.priority ? { priority: ev.priority } : {}),
      });
    }
  }

  for (const s of Object.values(state.sourceItems)) {
    if (s.archived) continue;
    const cal = sourceCalendar(state, s);
    if (!cal || !cal.visible) continue;
    if (kinds && !kinds.has(cal.kind)) continue;
    if (calIds && !calIds.has(cal.id)) continue;
    if (s.recurrenceId) continue; // Hanteras som override av huvudposten nedan.
    const ov = state.overlays[s.key];
    const overrides: Record<string, { timing: Timing; title?: string }> = {};
    for (const child of Object.values(state.sourceItems)) {
      if (child.sourceId === s.sourceId && child.uid === s.uid && child.recurrenceId && !child.archived) {
        overrides[child.recurrenceId] = { timing: child.data.timing, title: child.data.title };
      }
    }
    const catId = ov?.categoryId ?? 'myndighet';
    if (q && !textHit(s.data.summary, s.data.title, s.data.location, ov?.notes, state.categories[catId]?.name)) continue;
    for (const occ of expandOccurrences({ ...s.data, overrides }, from, toExclusive, tz)) {
      out.push({
        uid: `${s.key}@${occ.key}`,
        ref: { kind: 'source', key: s.key },
        occurrenceKey: occ.key,
        title: occ.override?.title ?? s.data.title,
        sourceSummary: s.data.summary,
        tag: s.data.tag,
        timing: occ.timing,
        calendarId: cal.id,
        calendarKind: cal.kind,
        calendarName: cal.name,
        color: cal.color,
        categoryId: catId,
        categoryName: state.categories[catId]?.name ?? 'Myndighet',
        icon: ov?.icon ?? 'landmark',
        ...(s.data.location ? { location: s.data.location } : {}),
        ...(ov?.notes ? { notes: ov.notes } : {}),
        done: !!ov?.done,
        transparent: s.data.transparent,
        locked: true,
        recurring: occ.recurring,
        adjusted: occ.adjusted,
        review: ov?.review?.reason ?? null,
        missingInLatest: s.presence === 'missing',
      });
    }
  }

  let items = out;
  if (filter.includeDone === false) items = items.filter((i) => !i.done);
  if (filter.query?.trim()) {
    const q = filter.query.trim().toLocaleLowerCase('sv');
    items = items.filter((i) =>
      [i.title, i.sourceSummary ?? '', i.categoryName, i.location ?? '', i.notes ?? ''].some((f) => f.toLocaleLowerCase('sv').includes(q)),
    );
  }
  return sortItems(items, tz);
}

/** Sortering: startdag, heldagar först, sedan starttid, sedan titel. Nycklar beräknas en gång per post. */
export function sortItems(items: DisplayItem[], tz: string): DisplayItem[] {
  const keyed = items.map((i) => ({
    i,
    day: startDay(i.timing, tz),
    allDay: i.timing.kind === 'allDay' ? 0 : 1,
    ms: i.timing.kind === 'allDay' ? 0 : timingInstants(i.timing, tz).start.epochMilliseconds,
  }));
  keyed.sort((a, b) => compareDates(a.day, b.day) || a.allDay - b.allDay || a.ms - b.ms || a.i.title.localeCompare(b.i.title, 'sv'));
  return keyed.map((k) => k.i);
}

/** Sök i titel, kategori, plats och anteckningar inom ett rimligt fönster. */
export function searchItems(state: AppState, query: string, today: ISODate, filter: ViewFilter = {}): DisplayItem[] {
  if (!query.trim()) return [];
  return collectItems(state, addDays(today, -366), addDays(today, 731), { ...filter, query }).slice(0, 200);
}

export function itemsOnDay(items: DisplayItem[], day: ISODate, tz: string): DisplayItem[] {
  return items.filter((i) => {
    if (i.timing.kind === 'allDay') return compareDates(i.timing.start, day) <= 0 && compareDates(day, i.timing.endExclusive) < 0;
    const { start, end } = timingInstants(i.timing, tz);
    const dayStart = Temporal.PlainDate.from(day).toZonedDateTime({ timeZone: tz }).toInstant();
    const dayEnd = Temporal.PlainDate.from(addDays(day, 1)).toZonedDateTime({ timeZone: tz }).toInstant();
    return Temporal.Instant.compare(start, dayEnd) < 0 && (Temporal.Instant.compare(end, dayStart) > 0 || Temporal.Instant.compare(start, dayStart) >= 0);
  });
}

/** Grupperar poster per kalenderdag i [from, from+days) i ett pass (agenda: O(poster), inte O(dagar × poster)). */
export function groupByDay(items: DisplayItem[], from: ISODate, days: number, tz: string): Map<ISODate, DisplayItem[]> {
  const toEx = addDays(from, days);
  const map = new Map<ISODate, DisplayItem[]>();
  for (const i of items) {
    for (const d of daysCovered(i.timing, tz)) {
      if (compareDates(d, from) < 0 || compareDates(d, toEx) >= 0) continue;
      const list = map.get(d);
      if (list) list.push(i);
      else map.set(d, [i]);
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Brådska – separat från taggar, kategori och prioritet
// ---------------------------------------------------------------------------

export type Urgency = 'passed' | 'today' | 'near' | 'upcoming' | 'done';

export interface DeadlineStatus {
  urgency: Urgency;
  label: string;
  daysLeft: number;
}

/**
 * "Idag", "2 dagar kvar", "Datum passerat, inte markerad klar här". AutoDay påstår aldrig att en
 * myndighet registrerat en försening – vi vet bara vad som är markerat här.
 */
export function deadlineStatus(date: ISODate, today: ISODate, done: boolean, nearDays = 3): DeadlineStatus {
  const daysLeft = daysBetween(today, date);
  if (done) return { urgency: 'done', label: 'Klar här (lokal kvittering)', daysLeft };
  if (daysLeft < 0) return { urgency: 'passed', label: 'Datum passerat, inte markerad klar här', daysLeft };
  if (daysLeft === 0) return { urgency: 'today', label: 'Idag', daysLeft };
  if (daysLeft === 1) return { urgency: 'near', label: 'Imorgon', daysLeft };
  return { urgency: daysLeft <= nearDays ? 'near' : 'upcoming', label: `${daysLeft} dagar kvar`, daysLeft };
}

/** "Viktigt": importerade skyldigheter och högprioriterade egna, närmast först, inklusive passerade ej klara. */
export function importantItems(state: AppState, today: ISODate, horizonDays = 120): Array<DisplayItem & { status: DeadlineStatus }> {
  const tz = state.settings.timeZone;
  const items = collectItems(state, addDays(today, -60), addDays(today, horizonDays));
  return items
    .filter((i) => i.locked || i.priority === 'high' || i.linkedSourceKey)
    .map((i) => ({ ...i, status: deadlineStatus(startDay(i.timing, tz), today, i.done) }))
    .filter((i) => i.status.urgency !== 'done' || i.status.daysLeft >= 0)
    .filter((i) => i.status.urgency !== 'passed' || !i.done)
    .sort((a, b) => a.status.daysLeft - b.status.daysLeft);
}

export function eventById(state: AppState, id: string): LocalEvent | undefined {
  const e = state.events[id];
  return e && !e.deleted ? e : undefined;
}
